import CryptoJS from 'crypto-js';

/**
 * Simple encryption utility for securing sensitive data like API keys using crypto-js
 * Compatible with frontend and backend implementations
 */
export class EncryptionUtil {
    private static readonly ALGORITHM = 'AES';
    private static readonly KEY_LENGTH = 256; // 256 bits
    private static readonly IV_LENGTH = 16; // 128 bits for AES
    private static readonly ITERATIONS = 100000; // PBKDF2 iterations

    /**
     * Encrypts a string value using AES
     * @param plaintext - The text to encrypt
     * @param masterKey - The master key used for encryption
     * @returns Encrypted string in format: salt:iv:encryptedData (base64 encoded)
     */
    static async encrypt(plaintext: string, masterKey: string): Promise<string> {
        try {
            // Generate a random salt
            const salt = CryptoJS.lib.WordArray.random(32);
            
            // Derive a key from the master key using PBKDF2
            const key = CryptoJS.PBKDF2(masterKey, salt, {
                keySize: this.KEY_LENGTH / 32, // keySize is in words (32 bits each)
                iterations: this.ITERATIONS,
                hasher: CryptoJS.algo.SHA256
            });
            
            // Generate a random IV
            const iv = CryptoJS.lib.WordArray.random(this.IV_LENGTH);
            
            // Encrypt the data using AES
            const encrypted = CryptoJS.AES.encrypt(plaintext, key, {
                iv: iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            });
            
            // Combine all components: salt:iv:encryptedData
            const result = salt.concat(iv).concat(encrypted.ciphertext);
            
            return result.toString(CryptoJS.enc.Base64);
        } catch (error) {
            console.error('Encryption error details:', error);
            throw new Error('Encryption failed');
        }
    }

    /**
     * Decrypts an encrypted string using AES
     * @param encryptedData - The encrypted data in format: salt:iv:encryptedData (base64 encoded)
     * @param masterKey - The master key used for decryption
     * @returns Decrypted string
     */
    static async decrypt(encryptedData: string, masterKey: string): Promise<string> {
        try {
            // Decode the base64 string
            const data = CryptoJS.enc.Base64.parse(encryptedData);
            
            // Extract components
            const salt = CryptoJS.lib.WordArray.create(data.words.slice(0, 8), 32); // 32 bytes = 8 words
            const iv = CryptoJS.lib.WordArray.create(data.words.slice(8, 12), 16); // 16 bytes = 4 words
            const encrypted = CryptoJS.lib.WordArray.create(data.words.slice(12), data.sigBytes - 48); // Rest of the data
            
            // Derive the key from the master key using PBKDF2
            const key = CryptoJS.PBKDF2(masterKey, salt, {
                keySize: this.KEY_LENGTH / 32, // keySize is in words (32 bits each)
                iterations: this.ITERATIONS,
                hasher: CryptoJS.algo.SHA256
            });
            
            // Create cipher params
            const cipherParams = CryptoJS.lib.CipherParams.create({
                ciphertext: encrypted
            });
            
            // Decrypt the data using AES
            const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
                iv: iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            });
            
            return decrypted.toString(CryptoJS.enc.Utf8);
        } catch (error) {
            console.error('Decryption error details:', error);
            throw new Error('Decryption failed - invalid key or corrupted data');
        }
    }

    /**
     * Generates a secure random master key for encryption/decryption
     * @param length - Length of the key in bytes (default: 32 bytes = 256 bits)
     * @returns Base64 encoded master key
     */
    static generateMasterKey(length: number = 32): string {
        const randomBytes = CryptoJS.lib.WordArray.random(length);
        return randomBytes.toString(CryptoJS.enc.Base64);
    }

    /**
     * Validates if a string is encrypted (basic check)
     * @param data - The data to check
     * @returns True if the data appears to be encrypted
     */
    static isEncrypted(data: string): boolean {
        try {
            const decoded = CryptoJS.enc.Base64.parse(data);
            // Check if the data has the minimum required length for our format
            return decoded.sigBytes >= 32 + this.IV_LENGTH + 1; // salt + iv + min data
        } catch {
            return false;
        }
    }
}

export default EncryptionUtil; 